/**
 * Cart Timer Component
 * A customizable urgency timer for the cart drawer
 */
class CartTimer extends HTMLElement {
  constructor() {
    super();
    this.duration = parseInt(this.getAttribute('duration') || '15') * 60; // Convert to seconds
    this.urgencyThreshold = parseInt(this.getAttribute('urgency-threshold') || '30'); // Percentage
    this.position = this.getAttribute('position') || 'top';
    this.intervalId = null;
    this.endTime = null;
    this.isUrgent = false;
    
    // Load saved timer state from sessionStorage
    this.loadState();
  }

  connectedCallback() {
    this.initializeTimer();
    this.startTimer();
    
    // Listen for cart drawer open/close events
    this.cartDrawer = document.querySelector('cart-drawer');
    if (this.cartDrawer) {
      // Use dialog events that Drawer extends from DialogElement
      this.cartDrawer.addEventListener('dialog:after-show', () => this.handleDrawerOpen());
      this.cartDrawer.addEventListener('dialog:after-hide', () => this.handleDrawerClose());
    }
  }

  disconnectedCallback() {
    this.stopTimer();
  }

  loadState() {
    const savedState = sessionStorage.getItem('cart-timer-state');
    if (savedState) {
      try {
        const state = JSON.parse(savedState);
        const now = Date.now();
        
        // Check if the saved endTime has passed
        if (state.endTime && now < state.endTime) {
          // Timer is still running, use the saved endTime
          this.endTime = state.endTime;
        } else {
          // Timer has expired, reset to full duration
          this.resetTimer();
        }
      } catch (e) {
        this.resetTimer();
      }
    } else {
      this.resetTimer();
    }
  }

  saveState() {
    if (this.endTime) {
      const state = {
        endTime: this.endTime,
        duration: this.duration
      };
      sessionStorage.setItem('cart-timer-state', JSON.stringify(state));
    }
  }

  resetTimer() {
    // Always reset to the full configured duration
    this.endTime = Date.now() + (this.duration * 1000);
    this.saveState();
  }

  initializeTimer() {
    if (!this.endTime) {
      this.resetTimer();
    }
    
    // Set position class
    this.classList.add(`cart-timer--position-${this.position}`);
    
    // Update display immediately
    this.updateDisplay();
  }

  startTimer() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    
    this.intervalId = setInterval(() => {
      this.updateDisplay();
      this.saveState();
    }, 1000);
    
    // Initial update
    this.updateDisplay();
  }

  stopTimer() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  getRemainingTime() {
    if (!this.endTime) return 0;
    const remaining = Math.max(0, Math.floor((this.endTime - Date.now()) / 1000));
    return remaining;
  }

  updateDisplay() {
    const remaining = this.getRemainingTime();
    
    if (remaining <= 0) {
      this.handleTimerExpired();
      return;
    }

    const minutes = Math.floor(remaining / 60);
    const seconds = remaining % 60;
    
    const minutesEl = this.querySelector('.cart-timer__minutes');
    const secondsEl = this.querySelector('.cart-timer__seconds');
    const messageEl = this.querySelector('.cart-timer__message');
    const urgencyMessageEl = this.querySelector('.cart-timer__urgency-message');
    const progressBarEl = this.querySelector('.cart-timer__progress-bar');
    
    if (minutesEl) minutesEl.textContent = String(minutes).padStart(2, '0');
    if (secondsEl) secondsEl.textContent = String(seconds).padStart(2, '0');
    
    // Calculate actual percentage remaining (for urgency logic)
    const actualPercentage = (remaining / this.duration) * 100;
    
    // Calculate progress bar percentage - starts at 100% and decreases to 0%
    // Fast 75% decrease in first 25% of time, then slow 25% decrease for remaining time
    let progressPercentage;
    const elapsedRatio = 1 - (remaining / this.duration); // 0 to 1 as time passes
    
    if (elapsedRatio <= 0.25) {
      // First 25% of time: progress bar goes from 100% to 25% quickly
      progressPercentage = 100 - (75 * (elapsedRatio / 0.25));
    } else {
      // Remaining 75% of time: progress bar goes from 25% to 0% slowly
      const remainingRatio = (elapsedRatio - 0.25) / 0.75;
      progressPercentage = 25 - (25 * remainingRatio);
    }
    
    // Check if we should show urgency state (based on actual time remaining)
    const shouldShowUrgency = actualPercentage <= this.urgencyThreshold;
    
    if (shouldShowUrgency !== this.isUrgent) {
      this.isUrgent = shouldShowUrgency;
      this.classList.toggle('cart-timer--urgent', this.isUrgent);
      
      if (messageEl && urgencyMessageEl) {
        messageEl.style.display = this.isUrgent ? 'none' : 'inline';
        urgencyMessageEl.style.display = this.isUrgent ? 'inline' : 'none';
      }
    }
    
    // Update progress bar if it exists (using the adjusted progress percentage)
    if (progressBarEl) {
      progressBarEl.style.setProperty('--progress', `${progressPercentage}%`);
    }
    
    // Add pulsing animation when very low (based on actual time)
    if (actualPercentage <= 10) {
      this.classList.add('cart-timer--critical');
    } else {
      this.classList.remove('cart-timer--critical');
    }
  }

  handleTimerExpired() {
    // Reset and restart the timer instead of stopping
    this.resetTimer();
    this.classList.remove('cart-timer--expired', 'cart-timer--urgent', 'cart-timer--critical');
    
    // Restore the normal message if it was changed
    const messageEl = this.querySelector('.cart-timer__message');
    const urgencyMessageEl = this.querySelector('.cart-timer__urgency-message');
    if (messageEl && urgencyMessageEl) {
      messageEl.style.display = 'inline';
      urgencyMessageEl.style.display = 'none';
    }
    
    // Update display immediately to show the new countdown
    this.updateDisplay();
  }

  handleDrawerOpen() {
    // Resume timer when drawer opens
    this.startTimer();
  }

  handleDrawerClose() {
    // Timer continues running in background (saved to sessionStorage)
    // No need to stop it
  }

  // Public method to reset timer (can be called externally)
  reset() {
    this.resetTimer();
    this.startTimer();
    this.classList.remove('cart-timer--expired', 'cart-timer--urgent', 'cart-timer--critical');
    this.updateDisplay();
  }
}

// Register the custom element
if (!window.customElements.get('cart-timer')) {
  window.customElements.define('cart-timer', CartTimer);
}

